import XLSXStyle from "xlsx-js-style"
import { Risk } from "@/features/risks/entities/risk/model/risk.types"
import { Establishment } from "@/features/adm/entities/establishment/model/establishment.types"
import { Organization } from "@/features/adm/entities/organization/model/organization.types"
import { Unit } from "@/features/risks/entities/unit/model/unit.types"

// ── Style helpers ─────────────────────────────────────────────────────────────

function headerStyle(bgHex: string): any {
  const rgb = bgHex.replace("#", "").toUpperCase()
  return {
    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 },
    fill: { fgColor: { rgb } },
    alignment: { vertical: "center", horizontal: "center", wrapText: true },
    border: {
      bottom: { style: "thin", color: { rgb: "AAAAAA" } },
      right:  { style: "thin", color: { rgb: "AAAAAA" } }
    }
  }
}

const CELL_ODD: any = {
  alignment: { vertical: "top", wrapText: true },
  border: { right: { style: "hair", color: { rgb: "DDDDDD" } } }
}
const CELL_EVEN: any = {
  fill: { fgColor: { rgb: "F4F7FB" } },
  alignment: { vertical: "top", wrapText: true },
  border: { right: { style: "hair", color: { rgb: "DDDDDD" } } }
}
const CELL_BOLD_ODD: any  = { ...CELL_ODD,  font: { bold: true } }
const CELL_BOLD_EVEN: any = { ...CELL_EVEN, font: { bold: true } }
const NUM_ODD: any  = { ...CELL_ODD,  alignment: { ...CELL_ODD.alignment,  horizontal: "center" } }
const NUM_EVEN: any = { ...CELL_EVEN, alignment: { ...CELL_EVEN.alignment, horizontal: "center" } }

const SCORE_COLORS: Record<string, string> = {
  "Faible": "27AE60",
  "Modéré": "D4A017",
  "Moyen":  "E67E22",
  "Élevé":  "C0392B"
}

function scoreStyle(label: string, isEven: boolean): any {
  const hex = SCORE_COLORS[label]
  return hex
    ? { font: { bold: true, color: { rgb: "FFFFFF" }, sz: 9 }, fill: { fgColor: { rgb: hex } }, alignment: { horizontal: "center", vertical: "center" } }
    : (isEven ? CELL_EVEN : CELL_ODD)
}

function textColorStyle(label: string, isEven: boolean): any {
  const hex = SCORE_COLORS[label]
  return hex
    ? { font: { bold: true, color: { rgb: hex }, sz: 9 }, alignment: { horizontal: "center", vertical: "center" }, ...(isEven ? { fill: { fgColor: { rgb: "F4F7FB" } } } : {}) }
    : (isEven ? CELL_EVEN : CELL_ODD)
}

// ── Sheet helpers ─────────────────────────────────────────────────────────────

function setCell(ws: any, r: number, c: number, v: any, s?: any) {
  const addr = XLSXStyle.utils.encode_cell({ r, c })
  ws[addr] = { v, t: typeof v === "number" ? "n" : "s", s }
}

function autoColWidths(matrix: any[][]): { wch: number }[] {
  const ncols = Math.max(...matrix.map((r) => r.length))
  return Array.from({ length: ncols }, (_, ci) => {
    const max = Math.max(...matrix.map((row) => String(row[ci] ?? "").length))
    return { wch: Math.min(max + 2, 55) }
  })
}

function updateRef(ws: any, nrows: number, ncols: number) {
  ws["!ref"] = XLSXStyle.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: nrows - 1, c: ncols - 1 } })
}

// ── Sheet 1: Récapitulatif ────────────────────────────────────────────────────

function buildRecapSheet(
  establishment: Establishment,
  organization: Organization,
  risks: Risk[],
  units: Unit[],
  headerBg: string
): any {
  const ws: any = {}
  const H = headerStyle(headerBg)
  let r = 0

  const titleRow = (label: string) => {
    setCell(ws, r, 0, label, H)
    setCell(ws, r, 1, "",    H)
    r++
  }
  const infoRow = (label: string, value: string | number) => {
    setCell(ws, r, 0, label, { font: { bold: true }, fill: { fgColor: { rgb: "EBF2FA" } } })
    setCell(ws, r, 1, value, { alignment: { vertical: "top", wrapText: true } })
    r++
  }

  titleRow("ÉTABLISSEMENT")
  infoRow("Nom", establishment.name)
  infoRow("Adresse", establishment.address || "—")
  infoRow("SIRET", establishment.establishmentId || "—")
  infoRow("Code NAF", establishment.industryCode || "—")
  infoRow("Organisation", organization.name)
  r++

  titleRow("DOCUMENT")
  infoRow("Unités de travail", units.length)
  infoRow("Risques identifiés", risks.length)
  r++

  titleRow("RÉPARTITION PAR NIVEAU DE RISQUE")
  const dist: Record<string, number> = {}
  for (const risk of risks) {
    const label = risk.valueEffectiveLabel || "—"
    dist[label] = (dist[label] || 0) + 1
  }
  for (const [label, count] of Object.entries(dist).sort((a, b) => b[1] - a[1])) {
    const hex = SCORE_COLORS[label]
    const s = hex
      ? { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: hex } }, alignment: { horizontal: "center" } }
      : undefined
    setCell(ws, r, 0, label, s)
    setCell(ws, r, 1, count, { alignment: { horizontal: "center" } })
    r++
  }

  ws["!cols"] = [{ wch: 28 }, { wch: 50 }]
  ws["!rows"] = Array.from({ length: r }, () => ({ hpt: 20 }))
  updateRef(ws, r, 2)
  return ws
}

// ── Sheet 2: Sommaire ─────────────────────────────────────────────────────────

function buildSommaireSheet(units: Unit[], risks: Risk[], headerBg: string): any {
  const ws: any = {}
  const H = headerStyle(headerBg)

  const headers = ["Unité de travail", "Collaborateurs", "Risques identifiés"]
  headers.forEach((h, c) => setCell(ws, 0, c, h, H))

  const countByUnit: Record<string, number> = {}
  for (const risk of risks) {
    const key = risk.unitName || "Sans unité"
    countByUnit[key] = (countByUnit[key] || 0) + 1
  }

  const sorted = [...units].sort((a, b) => (b.size ?? 0) - (a.size ?? 0))
  sorted.forEach((unit, i) => {
    const isEven = i % 2 === 0
    setCell(ws, i + 1, 0, unit.name,        isEven ? CELL_BOLD_EVEN : CELL_BOLD_ODD)
    setCell(ws, i + 1, 1, unit.size ?? "—", isEven ? NUM_EVEN : NUM_ODD)
    setCell(ws, i + 1, 2, countByUnit[unit.name] ?? 0, isEven ? NUM_EVEN : NUM_ODD)
  })

  const matrix = [headers, ...sorted.map((u) => [u.name, u.size ?? "—", countByUnit[u.name] ?? 0])]
  ws["!cols"] = autoColWidths(matrix)
  ws["!rows"] = [{ hpt: 28 }, ...sorted.map(() => ({ hpt: 20 }))]
  ws["!freeze"] = { xSplit: 0, ySplit: 1 }
  updateRef(ws, sorted.length + 1, 3)
  return ws
}

// ── Sheet per unit ────────────────────────────────────────────────────────────

function buildUnitSheet(unitRisks: Risk[], attributes: string[], headerBg: string): any {
  const ws: any = {}
  const H = headerStyle(headerBg)

  // Detect numeric columns for centering
  const numericAttrs = new Set(
    attributes.filter((attr) => {
      const vals = unitRisks.map((r) => String(r.attributes?.[attr] ?? "")).filter((v) => v !== "")
      return vals.length > 0 && vals.every((v) => v.trim() !== "" && !isNaN(Number(v)))
    })
  )

  // Columns: Type | attributes... | G | F | Risque brut | Formule brut | P | Risque résiduel | Formule résiduel | Commentaire
  const attrCols = attributes.map((attr) => ({ key: attr, label: attr }))
  const fixedCols = [
    { key: "rps",                 label: "Type" },
    { key: "severity",            label: "G" },
    { key: "frequency",           label: "F" },
    { key: "valueInitial",        label: "Risque brut" },
    { key: "_formulaBrut",        label: "Formule brut" },
    { key: "prevention",          label: "P" },
    { key: "valueEffective",      label: "Risque résiduel" },
    { key: "_formulaResiduel",    label: "Formule résiduel" },
    { key: "commentary",          label: "Commentaire" }
  ]
  const allCols = [...fixedCols.slice(0, 1), ...attrCols, ...fixedCols.slice(1)]

  allCols.forEach((col, c) => setCell(ws, 0, c, col.label, H))

  const sorted = [...unitRisks].sort((a, b) => (b.valueEffective ?? 0) - (a.valueEffective ?? 0))

  sorted.forEach((risk, i) => {
    const isEven = i % 2 === 0
    allCols.forEach((col, c) => {
      let val: any
      if (col.key === "rps") {
        val = risk.rps ? "RPS" : "PHY"
      } else if (col.key.startsWith("_formula")) {
        if (col.key === "_formulaResiduel") {
          val = `(${risk.severity}×${risk.frequency})÷${risk.prevention}`
        } else {
          val = `${risk.severity}×${risk.frequency}`
        }
      } else if (attrCols.some((a) => a.key === col.key)) {
        val = String(risk.attributes?.[col.key] ?? "")
      } else if (col.key === "valueEffective" || col.key === "valueInitial") {
        const raw = (risk as any)[col.key] as number | null
        val = raw != null ? Math.round(raw * 10) / 10 : ""
      } else {
        val = (risk as any)[col.key] ?? ""
      }

      let style: any
      if (col.key === "rps") {
        style = textColorStyle(risk.valueEffectiveLabel ?? "", isEven)
      } else if (col.key === "valueEffective") {
        style = scoreStyle(risk.valueEffectiveLabel ?? "", isEven)
      } else if (col.key === "valueInitial") {
        style = textColorStyle(risk.valueInitialLabel ?? "", isEven)
      } else if (col.key === "severity" || col.key === "frequency" || col.key === "prevention") {
        style = isEven ? NUM_EVEN : NUM_ODD
      } else if (col.key === "_formulaResiduel" || col.key === "_formulaBrut") {
        style = { ...(isEven ? CELL_EVEN : CELL_ODD), font: { italic: true, sz: 9 }, alignment: { horizontal: "center", vertical: "top" } }
      } else if (attrCols[0] && col.key === attrCols[0].key) {
        style = isEven ? CELL_BOLD_EVEN : CELL_BOLD_ODD
      } else if (numericAttrs.has(col.key)) {
        style = isEven ? NUM_EVEN : NUM_ODD
      } else {
        style = isEven ? CELL_EVEN : CELL_ODD
      }

      setCell(ws, i + 1, c, val, style)
    })
  })

  const matrix = [
    allCols.map((c) => c.label),
    ...sorted.map((risk) =>
      allCols.map((col) => {
        if (col.key === "rps") return risk.rps ? "RPS" : "PHY"
        if (col.key === "_formulaResiduel") return `(${risk.severity}×${risk.frequency})÷${risk.prevention}`
        if (col.key === "_formulaBrut")     return `${risk.severity}×${risk.frequency}`
        if (attrCols.some((a) => a.key === col.key)) return String(risk.attributes?.[col.key] ?? "")
        return (risk as any)[col.key] ?? ""
      })
    )
  ]

  ws["!cols"] = autoColWidths(matrix)
  ws["!rows"] = [{ hpt: 28 }, ...sorted.map(() => ({ hpt: 36 }))]
  ws["!freeze"] = { xSplit: 0, ySplit: 1 }
  updateRef(ws, sorted.length + 1, allCols.length)
  return ws
}

// ── Main export ───────────────────────────────────────────────────────────────

export function exportRisksToXlsx(
  risks: Risk[],
  establishment: Establishment,
  organization: Organization,
  units: Unit[],
  primaryColor = "#1A3A5C"
) {
  if (!risks || risks.length === 0) return

  const wb = XLSXStyle.utils.book_new()

  XLSXStyle.utils.book_append_sheet(wb, buildRecapSheet(establishment, organization, risks, units, primaryColor), "Récapitulatif")
  XLSXStyle.utils.book_append_sheet(wb, buildSommaireSheet(units, risks, primaryColor), "Sommaire")

  const byUnit = new Map<string, Risk[]>()
  for (const risk of risks) {
    const key = risk.unitName || "Sans unité"
    if (!byUnit.has(key)) byUnit.set(key, [])
    byUnit.get(key)!.push(risk)
  }

  const unitByName = new Map(units.map((u) => [u.name, u]))
  const sortedUnits = [...byUnit.entries()].sort((a, b) => {
    const sA = unitByName.get(a[0])?.size ?? 0
    const sB = unitByName.get(b[0])?.size ?? 0
    return (sB ?? 0) - (sA ?? 0)
  })

  for (const [unitName, unitRisks] of sortedUnits) {
    const sheetName = unitName.slice(0, 31).replace(/[\\/?*[\]:]/g, " ")
    XLSXStyle.utils.book_append_sheet(wb, buildUnitSheet(unitRisks, organization.riskAttributes, primaryColor), sheetName)
  }

  const safeName = (establishment.name ?? "export")
    .replace(/[^a-zA-Z0-9\u00C0-\u017E]/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 40)

  XLSXStyle.writeFile(wb, `document_unique_${safeName}.xlsx`)
}
